/**
 * The iManage area's frame: it reads the person's iManage connection and
 * shows the page asked for once they are connected. Until then it shows the
 * app's landing (IManageLanding) at the same address, so a deep link opened
 * while disconnected becomes its page as soon as the sign-in completes; while
 * the connection is unknown, a skeleton. Settings stays reachable, since it
 * is where an admin sets iManage up. The decision is appGate's, shared with
 * the layout, which leaves the area sidebar out beside the landing.
 *
 * Children read the organization, connection and chosen library from
 * IManageAreaContext.
 *
 * @module components/apps/imanage/IManageArea
 */
import { useLocation } from "@tanstack/react-router";
import { CloudIcon } from "lucide-react";
import { useCallback, type ReactNode } from "react";

import { useOrgProfile } from "../../../state/orgProfile";
import { useRegulationOrg } from "../../regulation/useRegulationOrg";
import { Skeleton } from "../../ui/skeleton";
import { useAppAreaView, useRememberAppView } from "../appAreaGates";
import { IManageAreaContext, type IManageAreaValue } from "./imanageAreaContext";
import { IManageLanding } from "./IManageLanding";
import { invalidateIManage, useIManageStatusQuery } from "./imanageQueries";
import { IManageMark } from "./IManageParts";

export function IManageArea({
  orgSegment,
  children,
}: {
  readonly orgSegment: string;
  readonly children: ReactNode;
}) {
  const { org, isLoading } = useRegulationOrg();
  const organizationId = org?.id ?? null;
  const { canManage } = useOrgProfile(organizationId);
  const status = useIManageStatusQuery(organizationId);
  const pathname = useLocation({ select: (location) => location.pathname });
  const href = useLocation({ select: (location) => location.href });
  const view = useAppAreaView(pathname, organizationId) ?? "loading";
  useRememberAppView("imanage", orgSegment, view, { pathname, href });
  const refreshStatus = useCallback(() => {
    if (organizationId) invalidateIManage(organizationId, ["status"]);
  }, [organizationId]);

  if (isLoading) return <AreaSkeleton />;
  if (!organizationId)
    return (
      <AreaMessage
        icon={<CloudIcon />}
        title="No organization selected"
        body="Choose an organization to open its iManage workspaces."
      />
    );
  if (view === "loading") return <AreaSkeleton />;
  if (view === "landing" || !status.data)
    return (
      <IManageLanding
        organizationId={organizationId}
        orgSegment={orgSegment}
        status={status.data}
        statusError={status.error}
        canManage={canManage}
        onRetry={status.refresh}
      />
    );
  const value: IManageAreaValue = {
    organizationId,
    orgSegment,
    status: status.data,
    canManage,
    refreshStatus,
  };
  return <IManageAreaContext.Provider value={value}>{children}</IManageAreaContext.Provider>;
}

export function AreaMessage({
  icon,
  title,
  body,
  action,
}: {
  readonly icon: ReactNode;
  readonly title: string;
  readonly body: string;
  readonly action?: ReactNode;
}) {
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center p-6" data-imanage-message="">
      <div className="flex max-w-sm flex-col items-center text-center">
        <div className="relative mb-5">
          <IManageMark className="size-11 rounded-xl" />
          <span className="absolute -right-1.5 -bottom-1.5 flex size-6 items-center justify-center rounded-full border border-border bg-background text-muted-foreground [&_svg]:size-3.5">
            {icon}
          </span>
        </div>
        <h1 className="text-[15px] font-semibold">{title}</h1>
        <p className="mt-1.5 text-sm text-pretty text-muted-foreground">{body}</p>
        {action ? <div className="mt-5">{action}</div> : null}
      </div>
    </div>
  );
}

function AreaSkeleton() {
  return (
    <div className="flex min-h-0 flex-1 flex-col" aria-busy="true" aria-label="Loading iManage">
      <div className="flex h-[52px] items-center gap-3 border-b border-border/60 px-6">
        <Skeleton className="h-4 w-48" />
      </div>
      <div className="flex flex-col gap-3 px-6 py-5">
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton key={i} className="h-4" style={{ width: `${70 - ((i * 7) % 30)}%` }} />
        ))}
      </div>
    </div>
  );
}
